/**
 * Single source of truth for ChatStatus wire-form parsing.
 *
 * Wave 2 PR-W2.2 of the SoT plan. Backend canonical = 5 UPPER_CASE
 * states (NEW / OPEN / PENDING / RESOLVED / CLOSED). Different
 * frontends choose different presentation taxonomies on top of this:
 *   - admin / storefront preserve all 5
 *   - floristy collapses to 3 ('new' / 'active' / 'closed')
 *
 * That collapse is a frontend presentation concern and stays out of
 * this contract — `parseChatStatus` only normalizes the wire form to
 * the canonical 5-state UPPER_CASE set. Each frontend then maps
 * canonical → its own taxonomy at the repository edge.
 */
const CANONICAL = new Set([
    'NEW',
    'OPEN',
    'PENDING',
    'RESOLVED',
    'CLOSED',
]);
/** Legacy lowercase form, accepted for the deprecation window. */
const LOWERCASE_LEGACY = Object.freeze(Object.fromEntries(Array.from(CANONICAL).map((v) => [v.toLowerCase(), v])));
/**
 * Parse user input into a canonical ChatStatus.
 *
 * Accepts canonical UPPER_CASE and lowercase legacy. Returns null on
 * unknown — caller decides whether to log/drop.
 */
export function parseChatStatus(raw) {
    if (typeof raw !== 'string')
        return null;
    if (CANONICAL.has(raw))
        return raw;
    return LOWERCASE_LEGACY[raw] ?? null;
}
/** Invariant: must already be canonical UPPER_CASE. Throws on dirty. */
export function assertChatStatus(s) {
    if (!CANONICAL.has(s)) {
        throw new Error(`Not a canonical ChatStatus: "${s}". Expected one of: NEW, OPEN, PENDING, RESOLVED, CLOSED.`);
    }
}
/** Narrow type guard — true only for canonical UPPER_CASE. */
export function isChatStatus(s) {
    return typeof s === 'string' && CANONICAL.has(s);
}
